import React, { useEffect, useState } from 'react';
import { BreadcrumbProps, Button, Icon, NumberDisplay, Tooltip } from '@aidkitorg/component-library';
import { useLocalizedStrings } from '../../Localization';
import { AdminPanelPage } from '../AdminPanelPage';
import { ApplicantsDataTable } from './components/ApplicantsDataTable';
import { useApplicantsContext } from '../../Contexts/ApplicantsContext';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { ApplicantExportModal } from './components/ApplicantExportModal';

export const AllApplicantsPage: React.FC = () => {
  const L = useLocalizedStrings();
  const history = useHistory();
  const { url } = useRouteMatch();
  const [exportModalOpen, setExportModalOpen] = useState<boolean>(false);

  const {
    logApplicantCreation,
    statsData,
    isStatsLoading,
    fetchApplicantStats
  } = useApplicantsContext();

  useEffect(() => {
    fetchApplicantStats();
  }, []);

  const breadcrumbs: BreadcrumbProps['items'] = [
    {
      label: L.admin_panel.nav.applicants
    },
    {
      label: L.admin_panel.nav.all_applicants
    }
  ];

  const totalCard = statsData?.hasConfiguration ? (
    <NumberDisplay
      loading={isStatsLoading}
      className="sm:max-w-64 min-w-64"
      title={L.admin_panel.applicants.all_applicants.stats.total_applicants}
      value={statsData?.total ?? 0}
      iconName="UsersIcon"
      iconColor="blue"
    />
  ) : null;

  const reviewingCard = statsData?.hasConfiguration && statsData?.inReview !== undefined ? (
    <NumberDisplay
      loading={isStatsLoading}
      className="sm:max-w-64 min-w-64"
      title={L.admin_panel.applicants.all_applicants.stats.reviewing}
      value={statsData.inReview}
      iconName="ClockIcon"
      iconColor="yellow"
    />
  ) : null;

  const fraudCard = statsData?.hasConfiguration && statsData?.fraud !== undefined ? (
    <NumberDisplay
      loading={isStatsLoading}
      className="sm:max-w-64 min-w-64"
      title={L.admin_panel.applicants.all_applicants.stats.marked_fraud}
      value={statsData.fraud}
      iconName="ExclamationTriangleIcon"
      iconColor="orange"
    />
  ) : null;

  const acceptedCard = statsData?.hasConfiguration && statsData?.accepted !== undefined ? (
    <NumberDisplay
      loading={isStatsLoading}
      className="sm:max-w-64 min-w-64"
      title={L.admin_panel.applicants.all_applicants.stats.accepted}
      value={statsData.accepted}
      iconName="CheckCircleIcon"
      iconColor="green"
    />
  ) : null;

  const rejectedCard = statsData?.hasConfiguration && statsData?.rejected !== undefined ? (
    <NumberDisplay
      loading={isStatsLoading}
      className="sm:max-w-64 min-w-64"
      title={L.admin_panel.applicants.all_applicants.stats.rejected}
      value={statsData.rejected}
      iconName="XCircleIcon"
      iconColor="red"
    />
  ) : null;

  return (
    <AdminPanelPage
      title={L.admin_panel.applicants.all_applicants.title}
      description={L.admin_panel.applicants.all_applicants.description}
      breadcrumbs={breadcrumbs}
      pageActionButtons={[
        <Tooltip text={L.admin_panel.applicants.all_applicants.buttons.refresh_statistics} asChild>
          <Button
            size="sm"
            variant='secondary'
            onClick={() => { fetchApplicantStats() }}
            icon={<Icon role="decorative" type="solid" name="ArrowPathIcon" />}
            aria-label={L.admin_panel.applicants.all_applicants.buttons.refresh_statistics}
          />
        </Tooltip>,
        <Button
          variant='primary'
          onClick={async () => {
            const creationLogged = await logApplicantCreation()

            if (creationLogged) {
              window.open('/apply')
            } else {
              return
            }
          }}
          icon={<Icon role="decorative" type="solid" name="UserPlusIcon" />}
          aria-label={'Add a new applicant'}
        >
          New Applicant
        </Button>,
        <Button
          variant='secondary'
          onClick={() => {
            history.push(`${url}/import`);
          }}
          icon={<Icon role="decorative" type="solid" name="ArrowUpTrayIcon" />}
          aria-label={L.admin_panel.applicants.all_applicants.buttons.import_applicants}
        >
          {L.admin_panel.applicants.all_applicants.buttons.import}
        </Button>,
        <Button
          variant='secondary'
          onClick={() => setExportModalOpen(true)}
          icon={<Icon role="decorative" type="solid" name="ArrowDownTrayIcon" />}
          aria-label={L.admin_panel.applicants.all_applicants.buttons.export_applicants}
        >
          {L.admin_panel.applicants.all_applicants.buttons.export}
        </Button>,
      ]}
    >
      <div className="w-full space-y-4 h-fit">
        {statsData?.hasConfiguration && (
          <div className="flex flex-wrap sm:flex-row gap-2 sm:gap-4">
            {totalCard}
            {reviewingCard}
            {acceptedCard}
            {rejectedCard}
            {fraudCard}
          </div>
        )}
        <ApplicantsDataTable />
      </div>
      <ApplicantExportModal
        exportModalOpen={exportModalOpen}
        setExportModalOpen={setExportModalOpen}
      />
    </AdminPanelPage>
  );
};

export default AllApplicantsPage;
